import { useClickAway } from 'ahooks'
import { motion, useDragControls } from 'framer-motion'
import React, { useRef } from 'react'
import ReactDOM from 'react-dom'

import { LayoutScroll } from 'components/LayoutPage'
import { IconButton } from 'components/uiKit/Button'
import { getDropDownContainer } from 'components/uiKit/Dropdown/constants'
import { KitSize } from 'components/uiKit/KitTypes'
import { getModalContainer } from 'components/uiKit/Modal/contants'

import { getFloatingPanelContainer } from '../constants'
import * as s from './Panel.module.scss'

const ANIMATIONS = {
  open: {
    opacity: 1,
    scale: 1,
  },
  closed: {
    opacity: 0,
    scale: 0.95,
  },
}

interface IPanelProps {
  children: React.ReactNode
  header?: React.ReactNode
  onClose: () => void
  refRoot: React.MutableRefObject<HTMLDivElement | null>
  width?: number | string
}

const Panel: React.FC<IPanelProps> = ({ refRoot, children, onClose, header, width }) => {
  const ref = useRef(null)

  const dragControls = useDragControls()

  useClickAway(
    (event) => {
      const target = event.target as HTMLElement
      const isModal = getModalContainer()?.contains(target)
      const isDropdown = Array.from(getDropDownContainer())?.some((f) => f.contains(target))
      const isFloating = Array.from(getFloatingPanelContainer())?.some((f) => f.contains(target))
      const isConnected = target.isConnected
      if (isConnected && !isDropdown && !isModal && !isFloating) {
        onClose()
      }
    },
    [ref, refRoot],
  )

  let styles = {}

  const asideElement = document.querySelector('[data-editor-panel="Toolbar"]') as HTMLElement
  if (asideElement) {
    const rect = asideElement.getBoundingClientRect()
    styles = {
      top: rect?.top ?? 100,
      right: (rect?.width ?? 100) + 12,
      width: width ?? 300,
    }
  }

  return ReactDOM.createPortal(
    <motion.div
      animate='open'
      className={s.wrapper}
      exit='closed'
      initial='closed'
      ref={ref}
      transition={{ duration: 0.2 }}
      variants={ANIMATIONS}
    >
      <motion.div
        className={s.popover}
        dragConstraints={ref}
        dragControls={dragControls}
        dragElastic={0}
        dragListener={false}
        dragMomentum={false}
        style={styles}
        drag
      >
        {header && (
          <div className={s.header} onPointerDown={(e) => dragControls.start(e)}>
            <div className={s.label}>{header}</div>
            <IconButton
              icon='otherClose'
              name='drawerClose'
              onClick={onClose}
              size={KitSize.S}
              styleType='ghost'
            />
          </div>
        )}
        <LayoutScroll sizeAutoCapable>
          <div className={s.content}>{children}</div>
        </LayoutScroll>
      </motion.div>
    </motion.div>,
    document.body,
  )
}

export default React.memo(Panel) as typeof Panel
